import { GraduationCap, ArrowRight, Clock, Check } from "lucide-react";
import { SectionHead, Stagger, StaggerItem, Reveal } from "@/lib/motion";
import { scrollToTarget } from "@/components/SmoothScroll";

const FORMATIONS = [
  {
    num: "01",
    title: "Formation certifiante en Pratique de Gestion des Ressources Humaines",
    prices: [
      { who: "Étudiant(e)", amt: "175 000" },
      { who: "Salarié(e)", amt: "250 000" },
    ],
  },
  {
    num: "02",
    title: "Formation certifiante en « JURISTE-RH »",
    prices: [
      { who: "Étudiant(e)", amt: "175 000" },
      { who: "Salarié(e)", amt: "250 000" },
    ],
  },
  {
    num: "03",
    title: "Formation certifiante en « RH-PAIE » et « COMPTABLE-PAIE »",
    prices: [
      { who: "Étudiant(e)", amt: "200 000" },
      { who: "Salarié(e)", amt: "300 000" },
    ],
  },
  {
    num: "04",
    title: "Formation certifiante « FISCALITÉ »",
    prices: [{ who: "Tarif unique", amt: "325 000" }],
  },
  {
    num: "05",
    title: "Formation certifiante « CHARGE DE RECOUVREMENT »",
    prices: [{ who: "Tarif unique", amt: "325 000" }],
  },
];

export default function Formations({ formations }) {
  const items = (formations && formations.length > 0)
    ? formations.map((f, i) => ({
        num: String(i + 1).padStart(2, "0"),
        title: f.title,
        prices: f.price_unique
          ? [{ who: "Tarif unique", amt: f.price_unique }]
          : [
              { who: "Étudiant(e)", amt: f.price_student },
              { who: "Salarié(e)", amt: f.price_salarie },
            ].filter((p) => p.amt),
        modules: f.modules || [],
        duration: f.duration,
      }))
    : FORMATIONS;
  return (
    <section id="formations" className="border-t border-white/10 py-24 md:py-32" data-testid="section-formations">
      <div className="mx-auto max-w-6xl px-5">
        <SectionHead
          tag="Nos formations"
          title="Formations certifiantes pour renforcer vos compétences."
          sub="Cinq parcours certifiants, ouverts aux étudiants et aux salariés."
        />
        <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((f) => (
            <StaggerItem key={f.num} className="h-full">
              <article
                className="group flex h-full flex-col rounded-2xl border border-white/10 border-t-2 border-t-korban-blue bg-white/[0.04] p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-korban-gold/40 hover:bg-white/[0.07]"
                data-testid={`formation-card-${f.num}`}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-korban-navy2/70 ring-1 ring-korban-gold/30">
                  <GraduationCap size={20} className="text-korban-gold" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold leading-snug text-white">{f.title}</h3>
                {f.duration ? (
                  <p className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-korban-blue/15 px-3 py-1 text-xs font-bold text-korban-blue">
                    <Clock size={12} /> Durée : {f.duration}
                  </p>
                ) : null}
                {f.modules && f.modules.length > 0 ? (
                  <ul className="mt-3 space-y-1.5">
                    {f.modules.map((m) => (
                      <li key={m} className="flex items-start gap-2 text-[0.85rem] leading-snug text-slate-300">
                        <Check size={12} className="mt-0.5 shrink-0 text-korban-gold" /> {m}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="mt-4 flex-1 space-y-2.5">
                  {f.prices.map((p) => (
                    <div
                      key={p.who}
                      className="flex items-center justify-between gap-3 rounded-lg bg-korban-navy2/40 px-4 py-2.5"
                    >
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-300">{p.who}</span>
                      <span className="whitespace-nowrap font-display text-lg font-bold text-korban-gold">
                        {p.amt} <small className="font-sans text-[0.6rem] font-semibold text-slate-400">FCFA</small>
                      </span>
                    </div>
                  ))}
                </div>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToTarget("#contact");
                  }}
                  data-testid={`formation-cta-${f.num}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-korban-blue transition-colors hover:text-korban-gold"
                >
                  S'inscrire à cette formation <ArrowRight size={15} />
                </a>
              </article>
            </StaggerItem>
          ))}
          <StaggerItem className="h-full">
            <Reveal delay={0.15} className="h-full">
              <div className="flex h-full flex-col items-start justify-center rounded-2xl border border-korban-gold/30 bg-gradient-to-br from-korban-navy2 to-korban-navy p-8">
                <p className="font-display text-2xl font-bold text-white">Un projet de formation sur mesure ?</p>
                <p className="mt-3 text-[0.95rem] leading-relaxed text-slate-300">
                  Parlez-nous de votre besoin : nous construisons avec vous le parcours adapté à votre équipe.
                </p>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToTarget("#contact");
                  }}
                  data-testid="formation-cta-custom"
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-korban-gold px-6 py-3 font-bold text-korban-navy transition-all hover:-translate-y-0.5 hover:bg-korban-golddark"
                >
                  Nous contacter <ArrowRight size={16} />
                </a>
              </div>
            </Reveal>
          </StaggerItem>
        </Stagger>
      </div>
    </section>
  );
}
